/**
 * @file   ack.js
 *         Implementation of Ack class. See grammar in spec for more information.
 * @author Wes Garland, wes@distributive.network
 * @date   Dec 2023
 */
'use strict';

exports.ackCtorFactory = function ackCtorFactory(connection)
{
  /**
   * Ack constructor. Acks are not DcpMessages, they are just objects that represent packets that
   * acknowledge the receipt of DcpMessages. These packets also carry the next nonce that the peer must
   * use to send us a DcpMessage. This class could easily be replaced by a vanilla object, except that
   * this gives us better debug output.
   *
   * Given that this class knows about connection internals, it should only be instantiated immediately
   * before it is transformed to network form.
   *
   * @constructor
   * @param {string} messageBody  The signed message body which is the network form of the DcpMessage we
   *                              are acknowledging receipt of
   * @param {string} nextNonce    The next nonce we expect to receive from the peer
   */
  function Ack(messageBody, nextNonce)
  {
    this.messageId = messageBody.id; /* Not part of the 5.3 spec; here for 5.2 compat and DCP-3957 */
    this.token = messageBody.ackToken;
    this.nonce = nextNonce;
  }

  /**
   * @returns {Object}   the network form of this Ack 
   */
  Ack.prototype.toNetwork = function Ack$$toNetwork()
  {
    return {
      type:      'ack',
      dcpsid:    connection.dcpsid,
      token:     this.token,      /* must match peer's lastTxAck.token */
      nonce:     this.nonce,      /* next message from peer must have this nonce */
      messageId: this.messageId
    };
  }

  /* Add fancy output for console.log in Node.js */
  if (require('dcp/common/dcp-env').platform === 'nodejs')
  {
    const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
    const inspect = requireNative('util').inspect.custom;
    Ack.prototype[inspect] = function Ack$$inspect()
    {
      let details = this.payload instanceof connection.ErrorPayload
          ? `${this.payload.name}: ${this.payload.message}`
          : this.payload?.operation || '';
      if (details)
        details = ':' + details;
      return `[Object ${String(connection.role)[0]}#${connection.seq}(Ack: ${this.token.slice(0,12)})]`;
    }
  }

  return Ack;
}
